// useSettingsRbac — 22's gates (contract 22 §12), the single place the legacy `isAdminOrOwner`
// triplication (`useWorkspaceSettings.js:17–19`, `AccountSettings.vue:91–93`, `Mfa.vue:101–103`)
// collapses into. Every gate reads 07's `useRbac()` — never a store, never a user id (audit
// ORP-18613 forbids the client re-deriving authority) — and stays cold-boot safe: pre-`ready` the
// account store's defaults make every gate false, so nothing renders enabled before RBAC exists.
//
// CM-22-06 is not landed: 07 exposes neither `accountLevel` nor `isAccountOwner` yet. Both are
// derived here from the surface 07 DOES publish — `rbac.is_account_holder` (server-computed, the
// authoritative form of §12's legacy id comparison) and `rbac.roles`, the account role labels
// `accountLevel` itself is computed from. When CM-22-06 lands, only the two derivations below
// change; the public shape callers see does not.
import { computed, type ComputedRef } from 'vue'

import { useRbac, type PermissionKey } from '@/features/account'

/** Role labels above `viewer` — `accountLevel !== 'viewer'` expressed on 07's current surface. */
const EDITOR_ROLES = new Set(['editor', 'admin', 'owner'])
const ADMIN_ROLES = new Set(['admin', 'owner'])

export interface UseSettingsRbac {
  /** Settings is unreachable for dashboard-only users (`SideNavItems.vue:248`). */
  canOpenSettings: ComputedRef<boolean>
  /** `accountLevel !== 'viewer'` (`useWorkspaceSettings.js:124`) — gates the workspace group. */
  canEditWorkspace: ComputedRef<boolean>
  canManageMembers: ComputedRef<boolean>
  canManageAccount: ComputedRef<boolean>
  isAccountOwner: ComputedRef<boolean>
  isAccountAdmin: ComputedRef<boolean>
  /**
   * The staff bypass (ledger A4). §12's table has no row for it because none of 22's own gates
   * needed one until ORP-19876: the Google-Sheet allow-list is gated on `is_token_owner`, a bit
   * that belongs to ONE user, and v3 let a sysadmin through it (`UsersPermissions.vue:136, :625`).
   *
   * ⚠ v3's `isSysAdmin` was a CLIENT user-id allowlist (`SYS_ADMIN_IDS`, the literal `10000403` its
   * test asserts) — ledger §C defect 7, deleted in v4 and guarded by `ledgerC.invariants.spec.ts`.
   * This reads 07's server-computed bit instead, so the BEHAVIOUR ports while the defect does not.
   */
  isSysAdmin: ComputedRef<boolean>
  can: (permission: PermissionKey) => boolean
}

export function useSettingsRbac(): UseSettingsRbac {
  const rbac = useRbac()

  const roles = computed(() => rbac.roles.value.map((role) => role.toLowerCase()))
  const isAccountOwner = computed(() => rbac.isAccountHolder.value)
  const isAccountAdmin = computed(
    () => isAccountOwner.value || roles.value.some((role) => ADMIN_ROLES.has(role)),
  )

  return {
    canOpenSettings: computed(() => !rbac.onlyDashboard.value),
    canEditWorkspace: computed(
      () => isAccountAdmin.value || roles.value.some((role) => EDITOR_ROLES.has(role)),
    ),
    canManageMembers: isAccountAdmin,
    canManageAccount: isAccountAdmin,
    isAccountOwner,
    isAccountAdmin,
    isSysAdmin: rbac.isSysAdmin,
    can: rbac.hasPermission,
  }
}
